import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

export default function Menu() {
    const user = useSelector((state) => state.Login.user);
    const [menuItems, setMenuItems] = useState([]);
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const normalizeAssociatedRoutes = (value, fallbackLink = null) => {
        const baseRoutes = Array.isArray(value)
            ? value
            : typeof value === 'string' && value.trim()
                ? [value.trim()]
                : [];

        if (fallbackLink && !baseRoutes.includes(fallbackLink)) {
            baseRoutes.push(fallbackLink);
        }

        return Array.from(new Set(baseRoutes.filter((route) => typeof route === 'string' && route.trim())));
    };

    useEffect(() => {
        if (user?.id) {
            const labelMap = {
                Operators: 'Drivers',
                'User Rol': 'User Permissions',
                'Configuration Alerts': 'Alerts',
                Services: 'Truck Maintenance',

            };

            const rawItems = Array.isArray(user.models) ? [...user.models] : [];
            const hasSupportItem = rawItems.some((item) => item?.link === '/support');

            if (!hasSupportItem) {
                rawItems.push({
                    id: 'support-dispatch',
                    name: 'Support',
                    icon: 'support_agent',
                    link: '/support',
                    menu: true,
                    associatedRoutes: ['/support'],
                });
            }

            const settlementIndex = rawItems.findIndex((item) => item?.link === '/settlements');
            const accountingIndex = rawItems.findIndex((item) => item?.link === '/accounting-settlements');

            if (settlementIndex >= 0 || accountingIndex >= 0) {
                const primaryIndex = settlementIndex >= 0 ? settlementIndex : accountingIndex;
                const primaryItem = {
                    ...rawItems[primaryIndex],
                    name: 'Settlements',
                    link: settlementIndex >= 0 ? '/settlements' : rawItems[primaryIndex]?.link,
                    associatedRoutes: Array.from(new Set([
                        ...normalizeAssociatedRoutes(
                            rawItems[primaryIndex]?.associatedRoutes,
                            rawItems[primaryIndex]?.link
                        ),
                        '/settlements',
                        '/accounting-settlements',
                        '/settlement/detail',
                        '/account-settlement',
                        '/addSettlements',
                    ])),
                };

                const normalizedItems = rawItems.filter((_, index) => index !== accountingIndex || accountingIndex === primaryIndex);
                const adjustedPrimaryIndex = accountingIndex >= 0 && accountingIndex < primaryIndex ? primaryIndex - 1 : primaryIndex;
                normalizedItems[adjustedPrimaryIndex] = primaryItem;

                rawItems.length = 0;
                rawItems.push(...normalizedItems);
            }

            const driverIndex = rawItems.findIndex((item) => item?.name === 'Drivers');
            const combinedSettlementsIndex = rawItems.findIndex((item) => item?.link === '/settlements' || item?.link === '/accounting-settlements');

            if (driverIndex >= 0 && combinedSettlementsIndex >= 0 && combinedSettlementsIndex !== driverIndex + 1) {
                const [settlementItem] = rawItems.splice(combinedSettlementsIndex, 1);
                rawItems.splice(driverIndex + 1, 0, settlementItem);
            }

            const supportIndex = rawItems.findIndex((item) => item?.link === '/support');

            if (supportIndex >= 0) {
                const [supportItem] = rawItems.splice(supportIndex, 1);
                rawItems.push(supportItem);
            }

            setMenuItems(
                rawItems.map((item) => ({
                    ...item,
                    associatedRoutes: normalizeAssociatedRoutes(item?.associatedRoutes, item?.link),
                    name: labelMap[item?.name] || item?.name,
                    _isIndented: item?.link === '/settlements' || item?.link === '/accounting-settlements',
                }))
            );
        }
    }, [user]);

    const toggleMenu = () => setIsMenuOpen((prev) => !prev);

    const isActiveRoute = (item) => {
        if (!item) return false;

        const current = location.pathname;
        const associatedRoutes = normalizeAssociatedRoutes(item?.associatedRoutes, item?.link);

        if (current === item.link) return true;

        if (associatedRoutes.some((route) => current === route || current.startsWith(`${route}/`))) {
            return true;
        }

        return false;
    };

    return (
        <div className="relative flex h-screen">
            {/* Top bar for mobile */}
            <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between bg-cs-bg border-b border-cs-border px-4 py-3 lg:hidden">
                <div className="flex items-center gap-2">
                    <img
                        src="/logoB.png"
                        alt="CargoSmart Logo"
                        className="h-8 w-auto object-contain"
                    />
                </div>
                <button
                    onClick={toggleMenu}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-cs-border text-cs-dark hover:bg-cs-bg-alt hover:text-cs-primary transition-colors"
                >
                    <span className="material-symbols-outlined text-base">
                        {isMenuOpen ? 'close' : 'menu'}
                    </span>
                </button>
            </div>

            {/* Sidebar */}
            <aside
                className={`
    fixed lg:static z-30
    top-[56px] lg:top-0
    left-0
    h-[calc(100vh-56px)] lg:h-full
    w-64
    bg-gradient-to-b from-cs-primary-dark to-cs-surface-dark
    border-r border-cs-primary/20
    flex flex-col
    transition-transform duration-200
    ${isMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
  `}
            >
                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                    {menuItems.map((item, index) => {
                        if (item.menu) {
                            const active = isActiveRoute(item);
                            return (
                                <Link
                                    key={index}
                                    to={item.link}
                                    className={`
            flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm transition-all
            ${active
                                            ? 'bg-white/10 text-white font-semibold'
                                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                                        }
          `}
                                >
                                    <span className="material-symbols-outlined text-[18px]">
                                        {item.icon}
                                    </span>
                                    <span className={`truncate ${item._isIndented ? 'pl-4 text-white/80' : ''}`}>
                                        {item.name}
                                    </span>
                                </Link>
                            );
                        }

                        return null;
                    })}
                </nav>

                <div className="px-4 py-3 border-t border-white/10 text-[10px] text-white/60 hidden lg:flex justify-between items-center">
                    <span>
                        Logged in as{' '}
                        <span className="font-medium text-white">
                            {user?.name || 'User'}
                        </span>
                    </span>
                </div>
            </aside>


            <div className="flex-1 lg:ml-0 pt-14 lg:pt-0">
                {/* Page content here */}
            </div>
        </div>
    );
}
